import React, {useEffect, useRef, useState} from "react";
import {deleteObject, getDownloadURL, getStorage, ref, uploadBytes} from "firebase/storage";
import {getDb} from "util/Constants";
import {Button, Col, Row, Spinner} from "reactstrap";
import CropImage from "../reusable/cropImage";
import {NotificationManager} from "react-notifications";
import {doc, updateDoc} from "firebase/firestore";

const EditArtwork = ({eventId, artWork}) => {
    const db = getDb();
    const storage = getStorage();

    const [logo, setLogo] = useState(null);
    const [localImage, setLocalImage] = useState(null);
    const [imageError, setImageError] = useState(null);


    const [video, setVideo] = useState(null);
    const [localVideo, setLocalVideo] = useState(null);

    const [loading, setLoading] = useState(false);
    const videoInputRef = useRef(null);

    useEffect(() => {
        setLogo(artWork.companyLogo || null);
        setLocalImage(null);
        setVideo(artWork.introVideo || null);
        setLocalVideo(null);
    }, [artWork]);

    const urlToFile = async (url, filename, mimeType) => {
        const res = await fetch(url);
        const buf = await res.arrayBuffer();
        return new File([buf], filename, {type: mimeType});
    };

    const uploadFile = async (file, path) => {
        const fileRef = ref(storage, path);
        const uploadResult = await uploadBytes(fileRef, file);
        return await getDownloadURL(uploadResult.ref);
    };

    const handleVideoUpload = (e) => {
        const file = e.target.files[0];
        if (file && file.size < 4 * 1024 * 1024) {
            setVideo(file);
            setLocalVideo(URL.createObjectURL(file));
        } else {
            NotificationManager.error("Video must be less than 4MB");
        }
    };

    const deleteVideo = () => {
        setVideo(null);
        setLocalVideo(null);
        if (videoInputRef.current) videoInputRef.current.value = "";
    };

    const deleteImage = () => {
        setLogo(null);
        setLocalImage(null);
    };

    const handleSubmit = async () => {
        if (!logo) {
            NotificationManager.error("Logo is required!");
            return;
        }

        setLoading(true);
        try {
            const eventRef = doc(db, "Events", eventId);
            const updates = {};

            // Handle logo upload
            if (typeof logo === "string") {
                updates.companyLogo = logo;
            } else {
                const fileToUpload = await urlToFile(localImage, `${eventId}-logo`, "image/png");
                updates.companyLogo = await uploadFile(fileToUpload, `eventAssets/${eventId}-logo`);
            }

            // Handle video upload or deletion
            if (!video) {
                await deleteObject(ref(storage, `eventAssets/${eventId}-intro-video`)).catch(() => {
                });
                updates.introVideo = "";
            } else if (typeof video !== "string") {
                updates.introVideo = await uploadFile(video, `eventAssets/${eventId}-intro-video`);
            }

            await updateDoc(eventRef, updates);
            NotificationManager.success("Assets updated successfully!");
        } catch (err) {
            console.error("Error updating assets:", err);
            NotificationManager.error("Failed to update assets.");
        }

        setLoading(false);
    };

    return (
        <>
            <Row>
                <Col sm={12} lg={6} className="px-4">
                    <label className="art-head">Update Company Logo*</label>
                    <label role="button" className="input-grey d-flex justify-content-between w-100 p-1 rounded">
                        <div className="py-2 pl-2">
                            <div className="d-flex">
                                <i className="fa fa-link mt-1 mr-2"/>
                                <span className="active-text text-underline font-weight-bold">
                                    {logo ? "File Selected" : "Choose file"}
                                </span>
                            </div>
                            <CropImage
                                aspectRatio={1}
                                stateSetImage={setLogo}
                                imageLocation="left"
                                setError={setImageError}
                                setLocal={setLocalImage}
                                maxSizeInKB={400}
                                sizeErrorMessage="Logo should be less than 400 KB!"
                            />
                        </div>
                        <div className="btn text-white align-items-center" style={{background: "#fba51b"}}>
                            Upload
                        </div>
                    </label>
                    <small className="text-muted font-weight-bold d-block">
                        Logo should be 150 x 150 px, 400 KB max
                    </small>
                </Col>

                <Col sm={12} lg={6} className="px-4">
                    <label className="art-head">Update Intro Video</label>
                    <label role="button" className="input-grey d-flex justify-content-between w-100 p-1 rounded">
                        <div className="py-2 pl-2">
                            <div className="d-flex">
                                <i className="fa fa-link mt-1 mr-2"/>
                                <span className="active-text text-underline font-weight-bold">
                                    {video?.name || "Choose file"}
                                </span>
                            </div>
                            <input
                                type="file"
                                className="d-none"
                                accept="video/mp4,video/*"
                                onChange={handleVideoUpload}
                                ref={videoInputRef}
                            />
                        </div>
                        <div className="btn text-white align-items-center" style={{background: "#fba51b"}}>
                            Upload
                        </div>
                    </label>
                    <small className="text-muted font-weight-bold d-block">
                        Video should be 720 x 720 px, 4 MB max
                    </small>
                </Col>
            </Row>

            <Row className="mt-3">
                <Col sm={12} lg={6} className="px-4">
                    {(localImage || logo) && (
                        <div className="d-flex align-items-center">
                            <img src={localImage || logo} alt="logo" width={100} height={100}/>
                            <Button className="ml-3" color="danger" size="sm" onClick={deleteImage}>
                                Delete
                            </Button>
                        </div>
                    )}
                </Col>

                <Col sm={12} lg={6} className="px-4">
                    {(localVideo || video) && (
                        <div className="d-flex align-items-center">
                            <video src={localVideo || video} width={300} controls/>
                            <Button className="ml-3" color="danger" size="sm" onClick={deleteVideo}>
                                Delete
                            </Button>
                        </div>
                    )}
                </Col>

                <Button color="success" size="sm" className="mt-3 ml-4" onClick={handleSubmit} disabled={loading}>
                    {loading ? <Spinner size="sm" color="light"/> : "Save"}
                </Button>
            </Row>
        </>
    );
};

export default EditArtwork;
